/* BOPF attribution capture
 * --------------------------------------------------------------------
 * Loaded synchronously in <head> (via theme.liquid) BEFORE any other
 * BOPF scripts. Captures UTM parameters and referrer info on every
 * page load and persists first-touch + last-touch attribution to
 * localStorage. On every page load, also syncs the attribution data
 * to Shopify's cart.attributes via /cart/update.js so it rides through
 * checkout to order.note_attributes, where the Render webhook handler
 * picks it up and writes a row to the Conversions sheet.
 *
 * Data model (localStorage key: __bopf_attr_v1):
 *   {
 *     first: { source, medium, campaign, term, content, landing, referrer, at },
 *     last:  { source, medium, campaign, term, content, landing, referrer, at },
 *     visits: <int>,           // sessions (30-min inactivity boundary)
 *     last_activity_ms: <int>  // last page-load timestamp
 *   }
 *
 * Last-touch updates only on visits arriving with UTM params or a
 * non-internal referrer. This matches GA4 convention - direct returns
 * to the site do not overwrite a meaningful prior source.
 *
 * If localStorage is unavailable (private browsing, blocked cookies),
 * the script silently no-ops. Attribution lookups then fall through
 * to "(direct)/(none)" downstream, which is correct.
 */
(function () {
  var KEY = '__bopf_attr_v1';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;

  // Hosts considered "internal". Referrers from these are ignored
  // (not treated as a new touch). Add CDN or Shopify checkout hosts
  // here if they ever start showing up as referrers.
  var INTERNAL_HOSTS = [
    'thebopf.com',
    'businessofprelovedfashion.myshopify.com',
    'checkout.shopify.com'
  ];

  // Known referrer hosts mapped to canonical source/medium. Keys are
  // lowercase, www-stripped. Anything not listed becomes
  // source=<host>, medium=referral.
  var REF_MAP = {
    'google.com':       { source: 'google',    medium: 'organic' },
    'google.ae':        { source: 'google',    medium: 'organic' },
    'google.co.uk':     { source: 'google',    medium: 'organic' },
    'bing.com':         { source: 'bing',      medium: 'organic' },
    'duckduckgo.com':   { source: 'duckduckgo', medium: 'organic' },
    'instagram.com':    { source: 'instagram', medium: 'social' },
    'l.instagram.com':  { source: 'instagram', medium: 'social' },
    'facebook.com':     { source: 'facebook',  medium: 'social' },
    'l.facebook.com':   { source: 'facebook',  medium: 'social' },
    'm.facebook.com':   { source: 'facebook',  medium: 'social' },
    'tiktok.com':       { source: 'tiktok',    medium: 'social' },
    't.co':             { source: 'twitter',   medium: 'social' },
    'twitter.com':      { source: 'twitter',   medium: 'social' },
    'x.com':            { source: 'twitter',   medium: 'social' },
    'linkedin.com':     { source: 'linkedin',  medium: 'social' },
    'pinterest.com':    { source: 'pinterest', medium: 'social' },
    'youtube.com':      { source: 'youtube',   medium: 'social' },
    'wa.me':            { source: 'whatsapp',  medium: 'referral' },
    'web.whatsapp.com': { source: 'whatsapp',  medium: 'referral' }
  };

  function readState() {
    try {
      var raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }
  function writeState(s) {
    try { window.localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* no-op */ }
  }

  function cleanHost(h) {
    return String(h || '').toLowerCase().replace(/^www\./, '');
  }
  function isInternal(host) {
    var h = cleanHost(host);
    for (var i = 0; i < INTERNAL_HOSTS.length; i++) {
      if (h === INTERNAL_HOSTS[i] || h.endsWith('.' + INTERNAL_HOSTS[i])) return true;
    }
    return false;
  }

  function getUtms() {
    try {
      var sp = new URL(window.location.href).searchParams;
      var u = {};
      ['source', 'medium', 'campaign', 'term', 'content'].forEach(function (k) {
        var v = sp.get('utm_' + k);
        if (v) u[k] = v.slice(0, 200);
      });
      // Click identifiers fall back to inferred source/medium if no
      // UTM params were set on the link.
      if (sp.get('gclid')) {
        u.source = u.source || 'google';
        u.medium = u.medium || 'cpc';
      }
      if (sp.get('fbclid')) {
        u.source = u.source || 'facebook';
        u.medium = u.medium || 'cpc';
      }
      if (sp.get('ttclid')) {
        u.source = u.source || 'tiktok';
        u.medium = u.medium || 'cpc';
      }
      return Object.keys(u).length ? u : null;
    } catch (e) { return null; }
  }

  function getReferrerInfo() {
    try {
      var ref = document.referrer;
      if (!ref) return null;
      var refUrl = new URL(ref);
      var host = cleanHost(refUrl.hostname);
      if (isInternal(host)) return null;
      var matched = REF_MAP[host];
      if (!matched) {
        // Try looser suffix match for sub-domains (e.g. 'mail.google.com')
        for (var k in REF_MAP) {
          if (REF_MAP.hasOwnProperty(k) && host.endsWith('.' + k)) { matched = REF_MAP[k]; break; }
        }
      }
      return {
        source: matched ? matched.source : host,
        medium: matched ? matched.medium : 'referral',
        host: host
      };
    } catch (e) { return null; }
  }

  // Build a touch from the current page load. Returns null if there's
  // no signal at all (no UTMs, no external referrer) - in that case
  // we don't update last-touch.
  function buildTouch() {
    var utms = getUtms();
    var ref = getReferrerInfo();
    var landing = '';
    try { landing = (window.location.pathname || '/').slice(0, 200); } catch (e) { landing = '/'; }
    var nowIso = new Date().toISOString();

    if (utms) {
      return {
        source:   utms.source   || (ref && ref.source) || '(direct)',
        medium:   utms.medium   || (ref && ref.medium) || '(none)',
        campaign: utms.campaign || '',
        term:     utms.term     || '',
        content:  utms.content  || '',
        landing:  landing,
        referrer: ref ? ref.host : '',
        at:       nowIso
      };
    }
    if (ref) {
      return {
        source:   ref.source,
        medium:   ref.medium,
        campaign: '',
        term:     '',
        content:  '',
        landing:  landing,
        referrer: ref.host,
        at:       nowIso
      };
    }
    return null;
  }

  // -- Run on every page load --------------------------------------
  var state = readState();
  var now   = Date.now();
  var touch = buildTouch();

  // Visit (session) bookkeeping. New session = >30min since last activity.
  if (!state.last_activity_ms || (now - state.last_activity_ms) > SESSION_TIMEOUT_MS) {
    state.visits = (state.visits || 0) + 1;
  }
  state.last_activity_ms = now;

  // First-touch is set once and never overwritten. If this is a direct
  // visit with no signal at all, we still seed first to '(direct)' so
  // downstream code always has a value to read.
  if (!state.first) {
    if (touch) {
      state.first = touch;
    } else {
      var landingPath = '/';
      try { landingPath = (window.location.pathname || '/').slice(0, 200); } catch (e) { /* no-op */ }
      state.first = {
        source:   '(direct)',
        medium:   '(none)',
        campaign: '', term: '', content: '',
        landing:  landingPath,
        referrer: '',
        at:       new Date().toISOString()
      };
    }
  }

  // Last-touch updates only when there's a real new signal. Direct
  // returns leave it as-is, preserving the prior meaningful source.
  if (touch) {
    state.last = touch;
  } else if (!state.last) {
    state.last = state.first;
  }

  writeState(state);

  // Sync to cart attributes. POST is async and intentionally not
  // awaited - if it fails (network blip, ad-blocker), the next page
  // load retries. The underscore prefix on each key tells Shopify to
  // hide these from the customer-facing cart UI.
  var attrs = {
    '_bopf_first_source':   state.first.source   || '',
    '_bopf_first_medium':   state.first.medium   || '',
    '_bopf_first_campaign': state.first.campaign || '',
    '_bopf_first_term':     state.first.term     || '',
    '_bopf_first_content':  state.first.content  || '',
    '_bopf_first_landing':  state.first.landing  || '',
    '_bopf_first_referrer': state.first.referrer || '',
    '_bopf_first_at':       state.first.at       || '',
    '_bopf_last_source':    state.last.source    || '',
    '_bopf_last_medium':    state.last.medium    || '',
    '_bopf_last_campaign':  state.last.campaign  || '',
    '_bopf_last_term':      state.last.term      || '',
    '_bopf_last_content':   state.last.content   || '',
    '_bopf_last_landing':   state.last.landing   || '',
    '_bopf_last_referrer':  state.last.referrer  || '',
    '_bopf_last_at':        state.last.at        || '',
    '_bopf_visits':         String(state.visits || 1)
  };

  // Defer the cart update slightly so it doesn't compete with critical
  // rendering paths or block the page's first paint.
  setTimeout(function () {
    try {
      fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ attributes: attrs })
      }).catch(function () { /* swallow - retried on next page load */ });
    } catch (e) { /* fetch unsupported (very old browser); skip */ }
  }, 250);

  // Expose for debugging / future scripts that want to read attribution.
  try { window.bopfAttribution = state; } catch (e) { /* no-op */ }
})();
